import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { config } from '../../../config';

const Intercom = ({ children }) => {
  const {
    company,
    intercomUserHash,
    isAuthenticated,
    profile,
  } = useSelector((state) => ({
    company: state.authentication.company,
    intercomUserHash: state.authentication.intercomUserHash,
    isAuthenticated: state.authentication.isAuthenticated,
    profile: state.authentication.profile,
  }));

  const getIntercomSettings = useCallback(() => {
    if (!profile || !company) return {};
    const { first_name: firstName, last_name: lastName, user_id: userId } = profile;
    const name = `${firstName} ${lastName}`;

    return {
      app_id: config.REACT_APP_INTERCOM_APP_ID,
      name,
      user_hash: intercomUserHash,
      user_id: userId,
      company: [{
        id: company.id,
        name: company.name,
      }],
    };
  }, [profile, intercomUserHash, company]);

  const bootIntercom = useCallback(() => {
    if (!intercomUserHash || !profile) return;
    const { user_id: userId } = profile;
    if (!userId) return;

    if (typeof window.Intercom === 'function') {
      window.Intercom('boot', getIntercomSettings());
    }
  }, [intercomUserHash, profile, getIntercomSettings]);

  const shutdownIntercom = useCallback(() => {
    if (typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
    }
  }, []);

  const updateIntercomCompanies = useCallback(() => {
    if (typeof window.Intercom === 'function') {
      window.Intercom('update', getIntercomSettings());
    }
  }, [getIntercomSettings]);

  // Run boot or shutdown when authentication status changes.
  useEffect(() => {
    if (isAuthenticated) {
      bootIntercom();
    } else {
      shutdownIntercom();
    }
  }, [isAuthenticated, bootIntercom, shutdownIntercom]);

  // Update companies when the companyId changes (and when authenticated).
  useEffect(() => {
    if (company?.id && isAuthenticated) {
      updateIntercomCompanies();
    }
  }, [company, isAuthenticated, updateIntercomCompanies]);

  return <>{children}</>;
};

Intercom.propTypes = {
  children: PropTypes.element.isRequired,
};

export default Intercom;
